import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface StatusColorPickerProps {
    value: string;
    isLoading: boolean;
    onChange: (color: string) => void;
    error?: boolean;
}

const PRESET_COLORS = [
    // Row 1 - Warm Colors (Reds, Oranges, Yellows)
    '#DC2626', // Bright Red
    '#EF4444', // Red
    '#F97316', // Orange
    '#EA580C', // Dark Orange
    '#F59E0B', // Yellow
    '#EAB308', // Amber
    '#D97706', // Dark Yellow
    '#F43F5E', // Rose
    '#E11D48', // Dark Rose
    '#BE185D', // Dark Pink

    // Row 2 - Neutral Colors (Purples, Magentas)
    '#C026D3', // Magenta
    '#EC4899', // Pink
    '#8B5CF6', // Purple
    '#7C3AED', // Violet
    '#9333EA', // Very Dark Purple
    '#6366F1', // Indigo
    '#4F46E5', // Dark Indigo
    '#3B82F6', // Blue
    '#2563EB', // Dark Blue
    '#1E40AF', // Very Dark Blue

    // Row 3 - Cool Colors (Blues, Greens, Cyans)
    '#0EA5E9', // Sky Blue
    '#0284C7', // Dark Sky Blue
    '#06B6D4', // Cyan
    '#0891B2', // Dark Cyan
    '#0E7490', // Very Dark Cyan
    '#10B981', // Green
    '#16A34A', // Dark Green
    '#22C55E', // Emerald Green
    '#84CC16', // Lime
    '#65A30D', // Dark Lime
];

export const StatusColorPicker: React.FC<StatusColorPickerProps> = ({
    value,
    onChange,
    isLoading,
    error = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [customColor, setCustomColor] = useState(value);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Update customColor when value prop changes
    useEffect(() => {
        setCustomColor(value);
    }, [value]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleColorSelect = (color: string) => {
        onChange(color);
        setCustomColor(color);
        setIsOpen(false);
    };

    const handleMainInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const color = e.target.value;
        setCustomColor(color);
        // Update form value immediately for better UX
        onChange(color);
    };

    const handleMainInputBlur = () => {
        // Validate and potentially revert if invalid
        if (!/^#[0-9A-F]{6}$/i.test(customColor)) {
            setCustomColor(value);
        }
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-3">
                <div className="relative" ref={dropdownRef}>
                    <Button
                        type="button"
                        variant="outline"
                        className={cn(
                            'w-12 h-12 p-0 border-2',
                            error ? 'border-destructive' : 'border-border'
                        )}
                        style={{ backgroundColor: value }}
                        onClick={() => {
                            console.log('Toggle dropdown, current state:', isOpen); // Debug log
                            setIsOpen(!isOpen);
                        }}
                    >
                        <span className="sr-only">Pick color</span>
                    </Button>

                    {isOpen && (
                        <div className="absolute top-full left-0 mt-2 w-80 p-4 bg-background border border-border rounded-lg shadow-lg z-50">
                            <div className="space-y-4">
                                <div>
                                    <Label className="text-sm font-medium">Preset Colors</Label>
                                    <div className="grid grid-cols-10 gap-2 mt-2">
                                        {PRESET_COLORS.map(color => (
                                            <button
                                                key={color}
                                                type="button"
                                                className={cn(
                                                    'w-8 h-8 rounded-full border-2 transition-all hover:scale-110',
                                                    value === color
                                                        ? 'border-white ring-2 ring-primary ring-offset-2'
                                                        : 'border-border hover:border-primary'
                                                )}
                                                style={{ backgroundColor: color }}
                                                onClick={() => {
                                                    console.log('Button clicked for color:', color); // Debug log
                                                    handleColorSelect(color);
                                                }}
                                                aria-label={`Select color ${color}`}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex-1">
                    <Input
                        type="text"
                        value={customColor}
                        disabled={isLoading}
                        onChange={handleMainInputChange}
                        onBlur={handleMainInputBlur}
                        placeholder="#000000"
                        className={cn('font-mono', error ? 'border-destructive' : '')}
                    />
                </div>
            </div>

            {error && (
                <p className="text-sm text-destructive">
                    Please enter a valid hex color code (e.g., #3B82F6)
                </p>
            )}
        </div>
    );
};
